import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  updateProfile,
  sendEmailVerification,
  reload,
  User as FirebaseUser
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  getDocsFromCache,
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  deleteDoc,
  runTransaction,
  onSnapshot
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import config from '../../firebase-applet-config.json';
import { UserProfile, TestAttempt, ForumPost, TutoringBooking, UserFileRecord, PraxisPaper, Question, StudyGuideRecord, QuestionAttempt, QuestionMastery, Flashcard, FlashcardReview, FlashcardMastery, EssayPrompt, ConstructedResponsePrompt, FeedEvent, PreviewUsage, Review, BlogPostRecord } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { APP_CONFIG } from '../config/appConfig';

const app = getApps().length === 0 ? initializeApp(config) : getApps()[0];
export const auth = getAuth(app);

// Initialize Firestore for custom database ID
// On-device cache (2026-10-08 speed round): Firestore keeps a copy of what
// it has already downloaded in the browser's IndexedDB, shared safely across
// tabs, so data read once (e.g. a paper's question pool) can be served from
// the device next time instead of downloaded again. If the browser cannot
// provide IndexedDB (some private windows), the SDK falls back to its normal
// in-memory behaviour; if Firestore was already initialised (hot reload),
// we reuse that instance.
function createFirestore() {
  try {
    return initializeFirestore(
      app,
      { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) },
      config.firestoreDatabaseId
    );
  } catch (cacheErr) {
    console.warn('[Firestore] Persistent cache unavailable, using default instance:', cacheErr);
    return getFirestore(app, config.firestoreDatabaseId);
  }
}
export const db = createFirestore();

export const storage = getStorage(app);

export { doc, onSnapshot };

// Default guest profile
export const DEFAULT_PROFILE: UserProfile = {
  uid: 'guest_user_1',
  displayName: 'Future Educator',
  email: 'future.teacher@praxisprep.edu',
  tier: 'free',
  unlockedPaperIds: [],
  targetState: 'ETS_NATIONAL',
  dailyGoalMinutes: 30,
  streakDays: 4,
  totalStudyMinutes: 210,
  createdAt: new Date().toISOString()
};

// --- SINGLE-DEVICE ACTIVE SESSION TOKEN MANAGEMENT ---

export const SESSION_TOKEN_STORAGE_KEY = 'praxis_active_session_token';

export function getLocalSessionToken(): string | null {
  try {
    const token = localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
    if (!token || typeof token !== 'string' || token === 'undefined' || token === 'null' || token.trim() === '') {
      try {
        localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
      } catch (_) {}
      return null;
    }
    return token.trim();
  } catch (e) {
    return null;
  }
}

export function setLocalSessionToken(token?: string | null): void {
  try {
    if (!token || typeof token !== 'string' || token === 'undefined' || token === 'null' || token.trim() === '') {
      localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
      return;
    }
    localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token.trim());
  } catch (e) {
    console.warn('[Session Manager] Could not store session token in localStorage:', e);
  }
}

export function clearLocalSessionToken(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

export function generateSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'sess_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
}

/**
 * Generates a new unique session token, persists it locally on this device,
 * and updates the user's Firestore profile document.
 */
export async function assignActiveSession(uid: string): Promise<string> {
  const token = generateSessionToken();
  setLocalSessionToken(token);
  const now = new Date().toISOString();

  try {
    const docRef = doc(db, 'users', uid);
    await setDoc(docRef, {
      activeSessionToken: token,
      activeSessionUpdatedAt: now
    }, { merge: true });
    const tokenDisplay = typeof token === 'string' ? (token.length > 8 ? token.substring(0, 8) : token) : 'token';
    console.log(`[Session Manager] Assigned new active session token for user ${uid}: ${tokenDisplay}...`);
  } catch (err) {
    console.warn('[Session Manager] Could not write active session token to Firestore:', err);
  }

  return token;
}

// --- AUTHENTICATION HELPERS ---

export async function signUpWithEmail(email: string, pass: string, name: string): Promise<UserProfile> {
  const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
  const user = userCredential.user;
  if (name) {
    await updateProfile(user, { displayName: name });
  }

  const token = generateSessionToken();
  setLocalSessionToken(token);
  const now = new Date().toISOString();

  const profile: UserProfile = {
    uid: user.uid,
    displayName: name || user.displayName || 'PRAXIS Educator',
    email: user.email || email,
    avatarUrl: user.photoURL || undefined,
    tier: 'free',
    unlockedPaperIds: [],
    targetState: 'ETS_NATIONAL',
    dailyGoalMinutes: 30,
    streakDays: 1,
    totalStudyMinutes: 0,
    createdAt: now,
    activeSessionToken: token,
    activeSessionUpdatedAt: now
  };

  await saveUserProfile(profile);

  // New sign-up flow: Firebase emails a verification link straight away.
  // Never blocks account creation -- the onboarding screen has its own
  // "Resend link" button if this first send fails.
  try {
    await sendEmailVerification(user, { url: `${window.location.origin}/?verified=1` });
  } catch (verifyErr) {
    console.warn('[Auth] Could not send the verification email right after sign-up:', verifyErr);
  }
  return profile;
}

export async function signInWithEmail(email: string, pass: string): Promise<UserProfile> {
  const userCredential = await signInWithEmailAndPassword(auth, email, pass);
  const user = userCredential.user;
  const token = await assignActiveSession(user.uid);
  let profile = await getUserProfile(user.uid);

  const now = new Date().toISOString();
  profile.uid = user.uid;
  profile.email = profile.email || user.email || email;
  if (!profile.displayName || profile.displayName === 'Future Educator' || profile.displayName === 'PRAXIS Educator') {
    profile.displayName = user.displayName || (profile.email ? profile.email.split('@')[0] : 'PRAXIS Educator');
  }
  profile.activeSessionToken = token;
  profile.activeSessionUpdatedAt = now;
  await saveUserProfile(profile);
  return profile;
}

export async function signInWithGoogle(): Promise<UserProfile> {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  const user = result.user;

  let profile = await getUserProfile(user.uid);
  const now = new Date().toISOString();
  const token = await assignActiveSession(user.uid);

  profile.uid = user.uid;
  profile.email = profile.email || user.email || '';
  if (!profile.displayName || profile.displayName === 'Future Educator' || profile.displayName === 'PRAXIS Educator') {
    profile.displayName = user.displayName || (user.email ? user.email.split('@')[0] : 'PRAXIS Educator');
  }
  if (!profile.avatarUrl && user.photoURL) {
    profile.avatarUrl = user.photoURL;
  }
  profile.activeSessionToken = token;
  profile.activeSessionUpdatedAt = now;
  await saveUserProfile(profile);
  return profile;
}

export async function signOutUser(): Promise<void> {
  clearLocalSessionToken();
  await signOut(auth);
}

export async function resetUserPassword(email: string): Promise<void> {
  const trimmed = email.trim();
  if (!trimmed) {
    throw new Error('Please enter your registered email address.');
  }
  await sendPasswordResetEmail(auth, trimmed);
}

export async function updateUserDisplayName(newName: string): Promise<UserProfile> {
  const current = auth.currentUser;
  if (!current) throw new Error('No user is currently signed in.');
  const trimmed = newName.trim();
  if (!trimmed) throw new Error('Display name cannot be empty.');

  await updateProfile(current, { displayName: trimmed });
  const profile = await getUserProfile(current.uid);
  profile.displayName = trimmed;
  await saveUserProfile(profile);
  return profile;
}

// --- FIREBASE STORAGE UPLOAD HELPER ---

export async function uploadFileToStorage(file: File, folder: string = 'uploads', userEmail?: string): Promise<{ url: string; name: string }> {
  try {
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `${folder}/${Date.now()}_${sanitizedName}`;
    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(snapshot.ref);

    // Save metadata record in Firestore 'user_files' collection for admin tracking
    try {
      const currentUid = auth.currentUser?.uid || 'guest_user_1';
      await addDoc(collection(db, 'user_files'), {
        userId: currentUid,
        userEmail: userEmail || auth.currentUser?.email || '',
        fileName: file.name,
        fileUrl: downloadUrl,
        folder: folder,
        size: file.size,
        uploadedAt: new Date().toISOString()
      });
    } catch (dbErr) {
      console.warn('Could not save user_file document:', dbErr);
    }

    return {
      url: downloadUrl,
      name: file.name
    };
  } catch (err) {
    console.error('Firebase Storage upload error:', err);
    throw err;
  }
}

// --- ADMIN DATA FETCHING HELPERS ---

export async function getAllUsersFromFirestore(): Promise<UserProfile[]> {
  try {
    const colRef = collection(db, 'users');
    const snap = await getDocs(colRef);
    return snap.docs.map(d => ({ uid: d.id, ...d.data() } as UserProfile));
  } catch (err) {
    console.error('Error fetching all users for admin:', err);
    return [];
  }
}

export async function getAllUserFilesFromFirestore(): Promise<UserFileRecord[]> {
  try {
    const colRef = collection(db, 'user_files');
    const snap = await getDocs(colRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as UserFileRecord));
  } catch (err) {
    console.error('Error fetching user files for admin:', err);
    return [];
  }
}

export async function getAllForumPostsFromFirestore(): Promise<ForumPost[]> {
  try {
    const colRef = collection(db, 'forum_posts');
    const snap = await getDocs(colRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as ForumPost));
  } catch (err) {
    console.error('Error fetching forum posts for admin:', err);
    return [];
  }
}

export async function getAllTutoringBookingsFromFirestore(): Promise<TutoringBooking[]> {
  try {
    const colRef = collection(db, 'tutoring_bookings');
    const snap = await getDocs(colRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as TutoringBooking));
  } catch (err) {
    console.error('Error fetching tutoring bookings for admin:', err);
    return [];
  }
}

export async function initAuthUser(): Promise<FirebaseUser | null> {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user || null);
      },
      (err) => {
        console.warn('[Auth] onAuthStateChanged error:', err);
        resolve(null);
      }
    );
  });
}

// --- EMAIL VERIFICATION + ONBOARDING (2026-10-07 sign-up flow) ---

// Only accounts created on or after this moment must verify their email.
// Existing students are left exactly as they were (agreed decision).
export const EMAIL_VERIFICATION_REQUIRED_FROM = '2026-10-07T00:00:00Z';

function accountCreatedAtMs(user: FirebaseUser | null | undefined): number {
  const created = Date.parse(user?.metadata?.creationTime || '');
  return isNaN(created) ? 0 : created;
}

// True for an account created after the new flow went live. Uses Firebase's
// own account creation time, which the student cannot edit.
export function isNewAccountForOnboarding(user: FirebaseUser | null | undefined = auth.currentUser): boolean {
  return accountCreatedAtMs(user) >= Date.parse(EMAIL_VERIFICATION_REQUIRED_FROM);
}

// Email/password accounts created under the new flow must click the
// verification link first. Google sign-ins are already verified by Google.
export function accountNeedsEmailVerification(user: FirebaseUser | null | undefined = auth.currentUser): boolean {
  if (!user || user.emailVerified) return false;
  const usesPassword = (user.providerData || []).some((p) => p?.providerId === 'password');
  if (!usesPassword) return false;
  return isNewAccountForOnboarding(user);
}

export async function sendVerificationLinkToCurrentUser(): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in again to resend the link.');
  await sendEmailVerification(user, { url: `${window.location.origin}/?verified=1` });
}

// Re-reads the account from Firebase. Once verified, also refreshes the ID
// token so the server sees email_verified: true straight away.
export async function refreshCurrentUserEmailVerified(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  await reload(user);
  const refreshed = auth.currentUser;
  if (refreshed?.emailVerified) {
    await refreshed.getIdToken(true);
    return true;
  }
  return false;
}

export interface OnboardingAnswers {
  targetPaperId?: string;
  targetExamFamily?: string;
  targetExamDate?: string | null;
  dailyGoalMinutes?: number;
  onboardingCompletedAt: string;
}

// Writes ONLY the screener fields (never the whole profile), and unlike
// saveUserProfile it throws on failure so the screen can show an error
// instead of silently losing the student's answers.
export async function saveOnboardingAnswers(uid: string, answers: OnboardingAnswers): Promise<void> {
  if (!uid || uid === 'guest_user_1') throw new Error('Please sign in first.');
  await setDoc(doc(db, 'users', uid), stripUndefined({ ...answers }), { merge: true });
}

// Asks the server to send the one-time welcome email. The server checks
// the token, the account age and whether it was already sent.
export async function requestWelcomeEmail(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const idToken = await user.getIdToken();
    await fetch('/api/email/welcome', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: '{}'
    });
  } catch (err) {
    console.warn('[Welcome Email] Request failed (non-blocking):', err);
  }
}

// Admin UIDs and emails that bypass paywalls unconditionally
export const ADMIN_UIDS = ['GBQme94c03e6XCBHGQEqF2eFBrF3'];
export const ADMIN_EMAILS = ['delightpeter12@gmail.com'];

export function isUserAdmin(profile?: UserProfile | null): boolean {
  if (!profile) return false;
  if (ADMIN_UIDS.includes(profile.uid)) return true;
  if (profile.role === 'admin' || profile.isAdmin === true) return true;
  if (profile.email && ADMIN_EMAILS.includes(profile.email.toLowerCase())) return true;
  return false;
}

export function isUserSubscribed(profile: UserProfile | null | undefined): boolean {
  // 0. Environment Bypass Flag (development / test mode only, defaults to false in prod)
  if (APP_CONFIG.BYPASS_PAYMENTS) return true;

  if (!profile) return false;
  // 1. Admin accounts bypass all paywalls for all content unconditionally
  if (isUserAdmin(profile)) return true;

  // 2. hasFullAccess check with non-expired subscription
  if (profile.hasFullAccess === true) {
    if (!profile.subscriptionEndDate) return true;
    try {
      const expiry = new Date(profile.subscriptionEndDate);
      if (!isNaN(expiry.getTime()) && expiry.getTime() > Date.now()) {
        return true;
      }
    } catch (_) {}
  }

  // 3. Active subscription tier check (tier in paid tiers and active status / valid expiry)
  const isPaidTier = ['basic', 'standard', 'extended', 'unlimited', 'all_access'].includes(profile.tier);
  if (isPaidTier) {
    if (profile.subscriptionEndDate) {
      try {
        const expiry = new Date(profile.subscriptionEndDate);
        if (!isNaN(expiry.getTime())) {
          return expiry.getTime() > Date.now();
        }
      } catch (_) {}
    }
    return profile.subscriptionStatus !== 'expired' && profile.subscriptionStatus !== 'cancelled';
  }

  return false;
}

/**
 * Access control check for all exam simulators, mock exams, and study guides.
 * Subscribed users (with hasFullAccess or active paid tier) unlock ALL exams platform-wide
 * across Praxis, CBEST, CSET, NYSTCE, TExES, FTCE, ILTS, PECT/PAPA, GACE, and all state standards.
 */
export function isPaperUnlockedForUser(profile: UserProfile | null | undefined, paperCodeOrId?: string): boolean {
  if (!profile) return false;

  // 1. Full subscriber access or admin access unlocks ALL papers across ALL exam families
  if (isUserSubscribed(profile)) return true;

  // 2. Check if a specific paper was individually unlocked (for individual paper purchase / free preview unlocks)
  if (paperCodeOrId && Array.isArray(profile.unlockedPaperIds)) {
    const normalized = String(paperCodeOrId).trim().toLowerCase();
    return profile.unlockedPaperIds.some(id => String(id).trim().toLowerCase() === normalized);
  }

  return false;
}

export async function getUserProfile(uid: string): Promise<UserProfile> {
  const authUser = auth.currentUser?.uid === uid ? auth.currentUser : null;
  try {
    const docRef = doc(db, 'users', uid);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const raw = snap.data();
      const rawEmail = raw.email || authUser?.email || '';
      const rawName = raw.displayName || authUser?.displayName || (rawEmail ? rawEmail.split('@')[0] : 'PRAXIS Educator');

      const data: UserProfile = {
        ...DEFAULT_PROFILE,
        ...raw,
        uid: raw.uid || snap.id || uid,
        email: rawEmail,
        displayName: rawName,
        avatarUrl: raw.avatarUrl || authUser?.photoURL || undefined
      };

      // If user is recognized admin, automatically ensure role/tier reflect full content access
      if (ADMIN_UIDS.includes(uid) || (data.email && ADMIN_EMAILS.includes(data.email.toLowerCase()))) {
        return {
          ...data,
          tier: 'unlimited',
          role: 'admin',
          isAdmin: true
        };
      }
      return data;
    }
  } catch (err) {
    console.warn('Error fetching Firestore user profile:', err);
  }

  const isAdm = ADMIN_UIDS.includes(uid) || (authUser?.email && ADMIN_EMAILS.includes(authUser.email.toLowerCase()));
  const derivedEmail = authUser?.email || '';
  const derivedName = authUser?.displayName || (derivedEmail ? derivedEmail.split('@')[0] : 'PRAXIS Educator');

  return {
    ...DEFAULT_PROFILE,
    uid,
    email: derivedEmail || DEFAULT_PROFILE.email,
    displayName: derivedName || DEFAULT_PROFILE.displayName,
    avatarUrl: authUser?.photoURL || undefined,
    tier: isAdm ? 'unlimited' : DEFAULT_PROFILE.tier,
    role: isAdm ? 'admin' : 'user',
    isAdmin: isAdm
  };
}

export async function saveUserProfile(profile: UserProfile): Promise<void> {
  try {
    const docRef = doc(db, 'users', profile.uid);
    // stripUndefined: Firestore rejects any field whose value is undefined
    // (this app does not enable ignoreUndefinedProperties). Email sign-ups
    // always carry avatarUrl: undefined, which used to make this whole
    // write fail silently, so their profile (and any goal exam) never saved.
    await setDoc(docRef, stripUndefined(profile), { merge: true });
  } catch (err) {
    console.warn('Error saving user profile to Firestore:', err);
  }
}

export async function saveTestResult(attempt: TestAttempt): Promise<void> {
  try {
    const colRef = collection(db, 'test_results');
    await addDoc(colRef, attempt);
  } catch (err) {
    console.warn('Error saving test attempt:', err);
  }
}

export async function getUserTestResults(userId: string): Promise<TestAttempt[]> {
  try {
    const colRef = collection(db, 'test_results');
    const q = query(colRef, where('userId', '==', userId), orderBy('completedAt', 'desc'), limit(20));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as TestAttempt));
  } catch (err) {
    console.warn('Firestore test results query fallback:', err);
    return [];
  }
}

export async function saveForumPost(post: Omit<ForumPost, 'id'>): Promise<string> {
  try {
    const colRef = collection(db, 'forum_posts');
    const res = await addDoc(colRef, post);
    return res.id;
  } catch (err) {
    console.warn('Error saving forum post:', err);
    return 'local_post_' + Date.now();
  }
}

export async function saveTutoringBooking(booking: Omit<TutoringBooking, 'id'>): Promise<string> {
  try {
    const colRef = collection(db, 'tutoring_bookings');
    const res = await addDoc(colRef, booking);
    return res.id;
  } catch (err) {
    console.warn('Error saving tutoring booking:', err);
    return 'local_booking_' + Date.now();
  }
}

// --- CONTENT MANAGER FIRESTORE HELPERS ---

export async function getPraxisPapersFromFirestore(): Promise<PraxisPaper[]> {
  try {
    const colRef = collection(db, 'praxisPapers');
    const snap = await getDocs(colRef);
    if (!snap.empty) {
      const papers: PraxisPaper[] = [];
      for (const docSnap of snap.docs) {
        const firestoreData = docSnap.data();
        const staticMatch = PRAXIS_PAPERS.find(p => p.id === docSnap.id || p.code === firestoreData.code);

        const paperData = {
          ...staticMatch,
          ...firestoreData,
          id: docSnap.id
        } as PraxisPaper;

        if (!paperData.domains || !Array.isArray(paperData.domains)) paperData.domains = staticMatch?.domains || [{ name: 'General', weightPercentage: 100 }];
        if (!paperData.highYieldTopics || !Array.isArray(paperData.highYieldTopics)) paperData.highYieldTopics = staticMatch?.highYieldTopics || [];
        if (!paperData.flashcards || !Array.isArray(paperData.flashcards)) paperData.flashcards = staticMatch?.flashcards || [];

        const qSnap = await getDocs(collection(db, 'praxisPapers', docSnap.id, 'questions'));
        if (!qSnap.empty) {
          paperData.questions = qSnap.docs.map(qd => ({ id: qd.id, paperId: docSnap.id, ...qd.data() } as Question));
          paperData.totalQuestions = (paperData.questions || []).length;
        } else if (!Array.isArray(paperData.questions) || paperData.questions.length === 0) {
          paperData.questions = staticMatch?.questions || [];
          paperData.totalQuestions = (paperData.questions || []).length;
        }
        papers.push(paperData);
      }
      return papers;
    }
  } catch (err) {
    console.warn('Error fetching praxisPapers from Firestore, falling back to static:', err);
  }
  return PRAXIS_PAPERS;
}

// Lightweight catalog read (2026-10-07): ONLY the praxisPapers documents
// themselves (title, code, category, totalQuestions, domains...), never the
// questions subcollections. Every visitor may read these docs, so unlike
// getPraxisPapersFromFirestore() above (whose per-paper question download
// is blocked for guests and free users, making the whole call fall back to
// the small static list), this returns the real, complete catalog for
// everyone, quickly. Used for exam-family paper counts and the onboarding
// exam picker. totalQuestions is kept up to date by the import endpoints.
//
// 2026-10-08: memoised for 5 minutes (several screens ask for it on the same
// visit), and the bundled static sample questions are no longer carried on
// each paper (questions: []), so screens that show "N questions" use the
// real stored totalQuestions instead of the small static sample count.
let catalogSummariesCache: { at: number; promise: Promise<PraxisPaper[]> } | null = null;
const CATALOG_SUMMARIES_TTL_MS = 5 * 60 * 1000;

export function getPaperCatalogSummaries(): Promise<PraxisPaper[]> {
  const now = Date.now();
  if (catalogSummariesCache && now - catalogSummariesCache.at < CATALOG_SUMMARIES_TTL_MS) {
    return catalogSummariesCache.promise;
  }
  const promise = (async (): Promise<PraxisPaper[]> => {
    try {
      const snap = await getDocs(collection(db, 'praxisPapers'));
      if (snap.empty) return PRAXIS_PAPERS;
      return snap.docs.map((docSnap) => {
        const data = docSnap.data();
        const staticMatch = PRAXIS_PAPERS.find(p => p.id === docSnap.id || p.code === data.code);
        const staticTotal = staticMatch?.totalQuestions || (Array.isArray(staticMatch?.questions) ? staticMatch!.questions.length : 0);
        const merged = { ...staticMatch, ...data, id: docSnap.id, questions: [] } as PraxisPaper;
        if (!Array.isArray(merged.domains)) merged.domains = staticMatch?.domains || [];
        if (!Array.isArray(merged.highYieldTopics)) merged.highYieldTopics = staticMatch?.highYieldTopics || [];
        if (!Array.isArray(merged.flashcards)) merged.flashcards = staticMatch?.flashcards || [];
        if (typeof merged.totalQuestions !== 'number') merged.totalQuestions = staticTotal;
        return merged;
      });
    } catch (err) {
      console.warn('Error fetching paper catalog summaries, falling back to static:', err);
      catalogSummariesCache = null; // allow a retry on the next call
      return PRAXIS_PAPERS;
    }
  })();
  catalogSummariesCache = { at: now, promise };
  return promise;
}

// One paper's details (title, code, timing, domains...) from its single
// praxisPapers document -- what the simulator needs before it starts,
// instead of downloading the whole catalog with every question in it.
// Falls back to the bundled static entry (including its sample questions,
// which the simulator uses only if the paper has no stored questions).
export async function getPaperMetadata(paperIdOrCode: string): Promise<PraxisPaper | null> {
  const staticMatch = PRAXIS_PAPERS.find(p => p.id === paperIdOrCode || p.code === paperIdOrCode) || null;
  try {
    const resolvedId = await resolvePaperDocId(paperIdOrCode);
    const snap = await getDoc(doc(db, 'praxisPapers', resolvedId));
    if (!snap.exists()) return staticMatch;
    const data = snap.data();
    const base = staticMatch || PRAXIS_PAPERS.find(p => p.code === data.code) || null;
    const merged = { ...base, ...data, id: snap.id } as PraxisPaper;
    if (!Array.isArray(merged.domains)) merged.domains = base?.domains || [];
    if (!Array.isArray(merged.highYieldTopics)) merged.highYieldTopics = base?.highYieldTopics || [];
    if (!Array.isArray(merged.flashcards)) merged.flashcards = base?.flashcards || [];
    if (!Array.isArray(merged.questions)) merged.questions = base?.questions || [];
    return merged;
  } catch (err) {
    console.warn(`Error fetching paper metadata for ${paperIdOrCode}:`, err);
    return staticMatch;
  }
}

export async function savePraxisPaperToFirestore(paper: Partial<PraxisPaper> & { id: string; code: string; title: string; domains?: any[] }): Promise<void> {
  try {
    const docRef = doc(db, 'praxisPapers', paper.id);
    let normalizedDomains = [{ name: 'General Domain', weightPercentage: 100 }];
    if (Array.isArray(paper.domains) && paper.domains.length > 0) {
      normalizedDomains = paper.domains.map((d: any) =>
        typeof d === 'string'
          ? { name: d, weightPercentage: Math.round(100 / paper.domains!.length) }
          : d
      );
    }

    const paperPayload: Record<string, any> = {
      id: paper.id,
      code: paper.code,
      title: paper.title,
      category: paper.category || 'core',
      contentType: paper.contentType || 'standard',
      examCode: paper.examCode || (paper.category === 'core' ? 'Praxis-Core' : paper.category === 'plt' ? 'Praxis-PLT' : 'Praxis-Subject'),
      durationMinutes: paper.durationMinutes !== undefined && !isNaN(Number(paper.durationMinutes)) ? Number(paper.durationMinutes) : 85,
      passingScoreEts: paper.passingScoreEts === null ? null : (paper.passingScoreEts !== undefined && !isNaN(Number(paper.passingScoreEts)) ? Number(paper.passingScoreEts) : 156),
      description: paper.description || '',
      domains: normalizedDomains,
      totalQuestions: paper.totalQuestions || 0,
      updatedAt: new Date().toISOString()
    };

    if (paper.scoreScaleMin !== undefined) {
      paperPayload.scoreScaleMin = Number(paper.scoreScaleMin);
    }
    if (paper.scoreScaleMax !== undefined) {
      paperPayload.scoreScaleMax = Number(paper.scoreScaleMax);
    }
    if (paper.visibleToStudents !== undefined) {
      paperPayload.visibleToStudents = paper.visibleToStudents;
    }
    if (paper.passingScoreNote !== undefined) {
      paperPayload.passingScoreNote = paper.passingScoreNote;
    }
    if (paper.isPassingScoreProvisional !== undefined) {
      paperPayload.isPassingScoreProvisional = paper.isPassingScoreProvisional;
    }
    if (paper.performanceTasks !== undefined) {
      paperPayload.performanceTasks = paper.performanceTasks;
    }
    if (paper.essayPrompts !== undefined) {
      paperPayload.essayPrompts = paper.essayPrompts;
    }
    if (paper.essayPrompt !== undefined) {
      paperPayload.essayPrompt = paper.essayPrompt;
    }
    if (paper.constructedResponsePrompts !== undefined) {
      paperPayload.constructedResponsePrompts = paper.constructedResponsePrompts;
    }
    await setDoc(docRef, paperPayload, { merge: true });
  } catch (err) {
    console.error('Error saving paper to Firestore:', err);
    throw err;
  }
}

// Helper to resolve the opaque Firestore document ID for a paper by id or code
const resolvedPaperIdCache = new Map<string, string>();

export async function resolvePaperDocId(paperIdOrCode: string): Promise<string> {
  if (!paperIdOrCode) return paperIdOrCode;
  const cachedId = resolvedPaperIdCache.get(paperIdOrCode);
  if (cachedId) return cachedId;
  const resolved = await resolvePaperDocIdUncached(paperIdOrCode);
  resolvedPaperIdCache.set(paperIdOrCode, resolved);
  return resolved;
}

async function resolvePaperDocIdUncached(paperIdOrCode: string): Promise<string> {
  try {
    const directDoc = await getDoc(doc(db, 'praxisPapers', paperIdOrCode));
    if (directDoc.exists()) {
      return directDoc.id;
    }
    // Query by code field if document ID differs from subtest code
    const codeQuery = query(collection(db, 'praxisPapers'), where('code', '==', paperIdOrCode));
    const codeSnap = await getDocs(codeQuery);
    if (!codeSnap.empty) {
      return codeSnap.docs[0].id;
    }
  } catch (e) {
    // Ignore and fallback
  }
  const staticMatch = PRAXIS_PAPERS.find(p => p.id === paperIdOrCode || p.code === paperIdOrCode);
  return staticMatch?.id || paperIdOrCode;
}

// ---- Question pools (2026-10-08 speed round) ----
// A paper's pool is fetched once per visit (memory cache) and, for up to
// QUESTION_POOL_DEVICE_TTL_MS after a successful server download, served
// from the on-device Firestore cache on later visits. Admin edits made
// through this app clear both caches for that paper right away; edits made
// elsewhere reach students within that window.
const questionPoolMemory = new Map<string, Promise<Question[]>>();
const QUESTION_POOL_DEVICE_TTL_MS = 6 * 60 * 60 * 1000;
const questionPoolStampKey = (paperDocId: string) => `pp_qpool_fetched_${paperDocId}`;

// Stamp = when the full pool was last downloaded from the server and how
// many questions it had. The device copy is only trusted if it is recent
// AND complete (the browser cache can evict old items when it fills up).
function readQuestionPoolStamp(paperDocId: string): { at: number; count: number } | null {
  try {
    const raw = localStorage.getItem(questionPoolStampKey(paperDocId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.at === 'number' && typeof parsed?.count === 'number') return parsed;
  } catch {
    /* unreadable stamp: treat as no stamp */
  }
  return null;
}

function writeQuestionPoolStamp(paperDocId: string, value: { at: number; count: number } | null) {
  try {
    if (value === null) localStorage.removeItem(questionPoolStampKey(paperDocId));
    else localStorage.setItem(questionPoolStampKey(paperDocId), JSON.stringify(value));
  } catch {
    /* storage unavailable: caching just becomes per-visit */
  }
}

export function invalidateQuestionCache(paperId: string) {
  const ids = new Set<string>([paperId]);
  const resolved = resolvedPaperIdCache.get(paperId);
  if (resolved) ids.add(resolved);
  ids.forEach((id) => {
    questionPoolMemory.delete(id);
    writeQuestionPoolStamp(id, null);
  });
}

// Stable order: by createdAt ascending (same order the old orderBy query
// returned), then by id; questions without createdAt are kept (the old
// orderBy query silently dropped them) and placed last.
function sortQuestionDocs(questions: Question[]): Question[] {
  return [...questions].sort((a: any, b: any) => {
    const ca = typeof a.createdAt === 'string' ? a.createdAt : '';
    const cb = typeof b.createdAt === 'string' ? b.createdAt : '';
    if (ca && cb && ca !== cb) return ca < cb ? -1 : 1;
    if (ca && !cb) return -1;
    if (!ca && cb) return 1;
    return String(a.id).localeCompare(String(b.id));
  });
}

async function loadQuestionPool(resolvedId: string): Promise<Question[]> {
  const colRef = collection(db, 'praxisPapers', resolvedId, 'questions');
  const toQuestions = (snap: any) =>
    sortQuestionDocs(snap.docs.map((qd: any) => ({ id: qd.id, paperId: resolvedId, ...qd.data() } as Question)));

  const stamp = readQuestionPoolStamp(resolvedId);
  if (stamp && stamp.count > 0 && Date.now() - stamp.at < QUESTION_POOL_DEVICE_TTL_MS) {
    try {
      const cached = await getDocsFromCache(colRef);
      if (cached.size >= stamp.count) return toQuestions(cached);
    } catch {
      /* nothing usable on the device: fall through to the server */
    }
  }

  const snap = await getDocs(colRef);
  if (!snap.empty) {
    writeQuestionPoolStamp(resolvedId, { at: Date.now(), count: snap.size });
    return toQuestions(snap);
  }
  return [];
}

export async function getQuestionsForPaperFromFirestore(paperId: string): Promise<Question[]> {
  const staticFallback = (): Question[] => {
    const staticPaper = PRAXIS_PAPERS.find(p => p.id === paperId || p.code === paperId);
    return staticPaper ? staticPaper.questions || [] : [];
  };
  try {
    const resolvedId = await resolvePaperDocId(paperId);
    let pending = questionPoolMemory.get(resolvedId);
    if (!pending) {
      pending = loadQuestionPool(resolvedId);
      questionPoolMemory.set(resolvedId, pending);
      // A failed or empty load must not be remembered for the whole visit.
      pending.then((qs) => { if (qs.length === 0) questionPoolMemory.delete(resolvedId); })
        .catch(() => questionPoolMemory.delete(resolvedId));
    }
    const questions = await pending;
    // A fresh array each time, so no caller can reorder the shared cached list.
    if (questions.length > 0) return [...questions];
  } catch (err) {
    console.warn(`Error fetching questions for paper ${paperId}:`, err);
  }
  return staticFallback();
}

// Question pools for just the papers this student has practised (plus any
// extra paper ids passed in, e.g. their goal exam), loaded in parallel.
// Used by the Dashboard and Review Queue, which previously downloaded every
// question of every paper to look up a few hundred the student had seen.
// Paper ids come from the mastery records (which store paperId from
// 2026-10-08 on) and, for older records, from the student's most recent
// practice attempts.
export async function getQuestionsForStudiedPapers(
  uid: string,
  mastery: QuestionMastery[] = [],
  extraPaperIds: string[] = []
): Promise<Question[]> {
  if (!uid || uid === 'guest_user_1') return [];
  const paperIds = new Set<string>(extraPaperIds.filter(Boolean));
  let missingPaperId = false;
  for (const m of mastery) {
    if ((m as any)?.paperId) paperIds.add((m as any).paperId);
    else missingPaperId = true;
  }
  if (missingPaperId || paperIds.size === 0) {
    try {
      const attemptsSnap = await getDocs(
        query(collection(db, 'users', uid, 'questionAttempts'), orderBy('timestamp', 'desc'), limit(1000))
      );
      attemptsSnap.docs.forEach((d) => {
        const pid = d.data()?.paperId;
        if (typeof pid === 'string' && pid) paperIds.add(pid);
      });
    } catch (err) {
      console.warn('Could not read recent question attempts for paper ids:', err);
    }
  }
  const pools = await Promise.all(
    Array.from(paperIds).map((pid) => getQuestionsForPaperFromFirestore(pid).catch(() => [] as Question[]))
  );
  const seen = new Set<string>();
  const out: Question[] = [];
  for (const pool of pools) {
    for (const q of pool) {
      if (q && q.id && !seen.has(q.id)) {
        seen.add(q.id);
        out.push(q);
      }
    }
  }
  return out;
}

export async function addQuestionToPaperInFirestore(paperId: string, question: Omit<Question, 'id'>): Promise<string> {
  try {
    invalidateQuestionCache(paperId);
    const colRef = collection(db, 'praxisPapers', paperId, 'questions');
    const res = await addDoc(colRef, {
      ...question,
      paperId,
      createdAt: new Date().toISOString()
    });
    const paperQuestions = await getQuestionsForPaperFromFirestore(paperId);
    await setDoc(doc(db, 'praxisPapers', paperId), { totalQuestions: paperQuestions.length }, { merge: true });
    return res.id;
  } catch (err) {
    console.error('Error adding question to paper:', err);
    throw err;
  }
}

export async function batchAddQuestionsToPaperInFirestore(paperId: string, questions: Omit<Question, 'id'>[]): Promise<number> {
  try {
    invalidateQuestionCache(paperId);
    const batch = writeBatch(db);
    const colRef = collection(db, 'praxisPapers', paperId, 'questions');

    for (const q of questions) {
      const newDocRef = doc(colRef);
      batch.set(newDocRef, {
        ...q,
        paperId,
        createdAt: new Date().toISOString()
      });
    }

    await batch.commit();
    const allQuestions = await getQuestionsForPaperFromFirestore(paperId);
    await setDoc(doc(db, 'praxisPapers', paperId), { totalQuestions: allQuestions.length }, { merge: true });

    // Companion Feed Event creation
    try {
      const matchedPaper = PRAXIS_PAPERS.find(p => p.id === paperId);
      const paperName = matchedPaper?.title || `Paper ${paperId}`;
      const examCode = matchedPaper?.code || paperId;
      await addFeedEvent({
        eventId: `event_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        examCode,
        type: 'content_added',
        title: `${questions.length} New Practice Questions Added to ${paperName}`,
        detail: `Fresh domain-aligned practice items updated in the question bank.`,
        timestamp: new Date().toISOString(),
        visibility: 'public'
      });
    } catch (feedErr) {
      console.warn('Could not write companion feed event:', feedErr);
    }

    return questions.length;
  } catch (err) {
    console.error('Error batch writing questions to Firestore:', err);
    throw err;
  }
}

export async function getTotalQuestionsCountFromFirestore(): Promise<number> {
  try {
    const papersCol = collection(db, 'praxisPapers');
    const snap = await getDocs(papersCol);
    if (!snap.empty) {
      let count = 0;
      for (const d of snap.docs) {
        const qSnap = await getDocs(collection(db, 'praxisPapers', d.id, 'questions'));
        count += qSnap.size;
      }
      if (count > 0) return count;
    }
  } catch (err) {
    console.warn('Error counting questions from Firestore:', err);
  }
  return PRAXIS_PAPERS.reduce((sum, p) => sum + (p.questions?.length || 0), 0);
}

export async function getStudyGuidesFromFirestore(paperId?: string): Promise<StudyGuideRecord[]> {
  try {
    const colRef = collection(db, 'studyGuides');
    let snap;
    if (paperId) {
      const q = query(colRef, where('paperId', '==', paperId));
      snap = await getDocs(q);
    } else {
      snap = await getDocs(colRef);
    }
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as StudyGuideRecord));
  } catch (err) {
    console.warn('Error fetching studyGuides from Firestore:', err);
    return [];
  }
}

export async function saveStudyGuideToFirestore(guide: Omit<StudyGuideRecord, 'id'> & { id?: string }): Promise<string> {
  try {
    if (guide.id) {
      const docRef = doc(db, 'studyGuides', guide.id);
      await setDoc(docRef, { ...guide, updatedAt: new Date().toISOString() }, { merge: true });
      return guide.id;
    } else {
      const colRef = collection(db, 'studyGuides');
      const res = await addDoc(colRef, { ...guide, updatedAt: new Date().toISOString() });
      return res.id;
    }
  } catch (err) {
    console.error('Error saving study guide to Firestore:', err);
    throw err;
  }
}

export async function deleteQuestionFromFirestore(paperId: string, questionId: string): Promise<void> {
  try {
    invalidateQuestionCache(paperId);
    const docRef = doc(db, 'praxisPapers', paperId, 'questions', questionId);
    await deleteDoc(docRef);
    const paperQuestions = await getQuestionsForPaperFromFirestore(paperId);
    await setDoc(doc(db, 'praxisPapers', paperId), { totalQuestions: paperQuestions.length }, { merge: true });
  } catch (err) {
    console.error('Error deleting question from Firestore:', err);
    throw err;
  }
}

export async function wipeAllQuestionsForPaperInFirestore(paperId: string): Promise<number> {
  try {
    invalidateQuestionCache(paperId);
    const colRef = collection(db, 'praxisPapers', paperId, 'questions');
    const snap = await getDocs(colRef);
    if (snap.empty) {
      return 0;
    }
    const batch = writeBatch(db);
    snap.docs.forEach((d) => {
      batch.delete(d.ref);
    });
    await batch.commit();

    // Update totalQuestions on paper doc to 0
    try {
      const paperDocRef = doc(db, 'praxisPapers', paperId);
      await setDoc(paperDocRef, { totalQuestions: 0, updatedAt: new Date().toISOString() }, { merge: true });
    } catch (e) {
      console.warn(`Could not update totalQuestions for paper ${paperId}:`, e);
    }

    console.log(`[wipeAllQuestionsForPaperInFirestore] Wiped ${snap.size} question docs from praxisPapers/${paperId}/questions.`);
    return snap.size;
  } catch (err) {
    console.error(`Error wiping questions for paper ${paperId}:`, err);
    throw err;
  }
}

export async function deletePaperFromFirestore(paperId: string): Promise<void> {
  try {
    const docRef = doc(db, 'praxisPapers', paperId);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting paper from Firestore:', err);
    throw err;
  }
}

export async function deleteStudyGuideFromFirestore(guideId: string): Promise<void> {
  try {
    const docRef = doc(db, 'studyGuides', guideId);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting study guide from Firestore:', err);
    throw err;
  }
}

// --- FLASHCARDS HELPERS ---
// Mirrors the question-pool architecture: shared cards live in a real
// subcollection under the paper doc (praxisPapers/{paperId}/flashcards),
// not a single embedded array field rewritten wholesale on every save.
// Personal cards (auto-generated from a student's own missed questions)
// live under users/{uid}/flashcards, same shape, scope: 'personal'.

function stripUndefined<T extends Record<string, any>>(obj: T): T {
  const out: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out as T;
}

export async function getFlashcardsForPaperFromFirestore(paperId: string): Promise<Flashcard[]> {
  try {
    const resolvedId = await resolvePaperDocId(paperId);
    const colRef = collection(db, 'praxisPapers', resolvedId, 'flashcards');
    const q = query(colRef, orderBy('createdAt', 'asc'));
    let snap = await getDocs(q);

    // Fallback if legacy documents lack createdAt or indexing
    if (snap.empty) {
      snap = await getDocs(colRef);
    }

    if (!snap.empty) {
      return snap.docs.map(fd => ({ id: fd.id, paperId: resolvedId, scope: 'shared', ...fd.data() } as Flashcard));
    }
  } catch (err) {
    console.warn(`Error fetching flashcards for paper ${paperId} (trying unindexed fallback):`, err);
    try {
      const resolvedId = await resolvePaperDocId(paperId);
      const colRef = collection(db, 'praxisPapers', resolvedId, 'flashcards');
      const snap = await getDocs(colRef);
      if (!snap.empty) {
        return snap.docs.map(fd => ({ id: fd.id, paperId: resolvedId, scope: 'shared', ...fd.data() } as Flashcard));
      }
    } catch (fallbackErr) {
      console.error(`Fallback flashcard fetch failed for paper ${paperId}:`, fallbackErr);
    }
  }
  const staticPaper = PRAXIS_PAPERS.find(p => p.id === paperId || p.code === paperId);
  return staticPaper ? (staticPaper.flashcards || []).map(c => ({ ...c, scope: 'shared' as const })) : [];
}

export async function saveFlashcardToFirestore(paperId: string, flashcard: Omit<Flashcard, 'id'> & { id?: string }): Promise<string> {
  try {
    const resolvedId = await resolvePaperDocId(paperId);
    const colRef = collection(db, 'praxisPapers', resolvedId, 'flashcards');
    const now = new Date().toISOString();
    const payload = stripUndefined({
      paperId: resolvedId,
      domain: flashcard.domain || 'Core Vocabulary',
      term: flashcard.term,
      definition: flashcard.definition,
      exampleScenario: flashcard.exampleScenario || '',
      sourceQuestionId: flashcard.sourceQuestionId,
      scope: 'shared' as const,
      generatedBy: flashcard.generatedBy || 'admin',
      updatedAt: now
    });

    if (flashcard.id) {
      const docRef = doc(colRef, flashcard.id);
      await setDoc(docRef, payload, { merge: true });
      return flashcard.id;
    } else {
      const res = await addDoc(colRef, { ...payload, createdAt: now });
      return res.id;
    }
  } catch (err) {
    console.error('Error saving flashcard to Firestore:', err);
    throw err;
  }
}

export async function deleteFlashcardFromFirestore(paperId: string, flashcardId: string): Promise<void> {
  try {
    const resolvedId = await resolvePaperDocId(paperId);
    const docRef = doc(db, 'praxisPapers', resolvedId, 'flashcards', flashcardId);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting flashcard from Firestore:', err);
    throw err;
  }
}

/**
 * Personal deck: users/{uid}/flashcards/{flashcardId} -- private cards, usually
 * auto-generated from that one student's own missed questions. Optionally
 * filtered to one paper.
 */
export async function getPersonalFlashcardsForUser(uid: string, paperId?: string): Promise<Flashcard[]> {
  if (!uid || uid === 'guest_user_1') return [];
  try {
    const colRef = collection(db, 'users', uid, 'flashcards');
    const snap = paperId
      ? await getDocs(query(colRef, where('paperId', '==', paperId)))
      : await getDocs(colRef);
    return snap.docs.map(fd => ({ id: fd.id, scope: 'personal', ...fd.data() } as Flashcard));
  } catch (err) {
    console.warn('Error fetching personal flashcards from Firestore:', err);
    return [];
  }
}

export async function savePersonalFlashcardToFirestore(uid: string, flashcard: Omit<Flashcard, 'id'> & { id?: string }): Promise<string> {
  try {
    const colRef = collection(db, 'users', uid, 'flashcards');
    const now = new Date().toISOString();
    const payload = stripUndefined({
      paperId: flashcard.paperId,
      domain: flashcard.domain || 'Core Vocabulary',
      term: flashcard.term,
      definition: flashcard.definition,
      exampleScenario: flashcard.exampleScenario || '',
      sourceQuestionId: flashcard.sourceQuestionId,
      scope: 'personal' as const,
      generatedBy: flashcard.generatedBy || 'auto-from-mistake',
      updatedAt: now
    });

    if (flashcard.id) {
      const docRef = doc(colRef, flashcard.id);
      await setDoc(docRef, payload, { merge: true });
      return flashcard.id;
    } else {
      const res = await addDoc(colRef, { ...payload, createdAt: now });
      return res.id;
    }
  } catch (err) {
    console.error('Error saving personal flashcard to Firestore:', err);
    throw err;
  }
}

export async function deletePersonalFlashcardFromFirestore(uid: string, flashcardId: string): Promise<void> {
  try {
    const docRef = doc(db, 'users', uid, 'flashcards', flashcardId);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting personal flashcard from Firestore:', err);
    throw err;
  }
}

// --- ESSAY PROMPTS HELPERS ---

export async function getEssayPromptsForPaperFromFirestore(paperId: string): Promise<EssayPrompt[]> {
  try {
    const resolvedId = await resolvePaperDocId(paperId);
    const paperRef = doc(db, 'praxisPapers', resolvedId);
    const paperSnap = await getDoc(paperRef);
    if (paperSnap.exists()) {
      const data = paperSnap.data();
      if (Array.isArray(data.essayPrompts) && data.essayPrompts.length > 0) {
        return data.essayPrompts as EssayPrompt[];
      }
      // Backward-compatibility: if legacy single essayPrompt exists on Firestore doc
      if (data.essayPrompt && data.essayPrompt.promptText) {
        return [{
          id: data.essayPrompt.id || `ep_${resolvedId}_1`,
          paperId: resolvedId,
          type: (data.essayPrompt.type || 'argumentative') as 'argumentative' | 'informative-explanatory',
          title: data.essayPrompt.title || 'Writing Task',
          promptText: data.essayPrompt.promptText,
          timeLimitMinutes: data.essayPrompt.timeLimitMinutes || 30,
          sample6Response: data.essayPrompt.sample6Response || '',
          rubricNotes: data.essayPrompt.rubricNotes || ''
        }];
      }
    }
  } catch (err) {
    console.warn(`Error fetching essay prompts for paper ${paperId}:`, err);
  }
  const staticPaper = PRAXIS_PAPERS.find(p => p.id === paperId || p.code === paperId);
  if (staticPaper) {
    if (Array.isArray(staticPaper.essayPrompts) && staticPaper.essayPrompts.length > 0) {
      return staticPaper.essayPrompts;
    }
    if (staticPaper.essayPrompt && staticPaper.essayPrompt.promptText) {
      return [{
        id: staticPaper.essayPrompt.id || `ep_${staticPaper.id}_1`,
        paperId: staticPaper.id,
        type: 'argumentative',
        title: staticPaper.essayPrompt.title || 'Writing Task',
        promptText: staticPaper.essayPrompt.promptText,
        timeLimitMinutes: 30,
        sample6Response: staticPaper.essayPrompt.sample6Response || '',
        rubricNotes: staticPaper.essayPrompt.rubricNotes || ''
      }];
    }
  }
  return [];
}

export async function saveEssayPromptToFirestore(paperId: string, prompt: Omit<EssayPrompt, 'id'> & { id?: string }): Promise<string> {
  try {
    const currentPrompts = await getEssayPromptsForPaperFromFirestore(paperId);
    const promptId = prompt.id || `ep_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const newPrompt: EssayPrompt = {
      id: promptId,
      paperId,
      type: prompt.type || 'argumentative',
      title: prompt.title,
      promptText: prompt.promptText,
      timeLimitMinutes: prompt.timeLimitMinutes || 30,
      sample6Response: prompt.sample6Response || '',
      rubricNotes: prompt.rubricNotes || ''
    };

    const existingIdx = currentPrompts.findIndex(p => p.id === promptId);
    let updated: EssayPrompt[];
    if (existingIdx >= 0) {
      updated = [...currentPrompts];
      updated[existingIdx] = newPrompt;
    } else {
      updated = [...currentPrompts, newPrompt];
    }

    const paperRef = doc(db, 'praxisPapers', paperId);
    await setDoc(paperRef, { essayPrompts: updated }, { merge: true });
    return promptId;
  } catch (err) {
    console.error('Error saving essay prompt to Firestore:', err);
    throw err;
  }
}

export async function deleteEssayPromptFromFirestore(paperId: string, promptId: string): Promise<void> {
  try {
    const currentPrompts = await getEssayPromptsForPaperFromFirestore(paperId);
    const updated = currentPrompts.filter(p => p.id !== promptId);
    const paperRef = doc(db, 'praxisPapers', paperId);
    await setDoc(paperRef, { essayPrompts: updated }, { merge: true });
  } catch (err) {
    console.error('Error deleting essay prompt from Firestore:', err);
    throw err;
  }
}

// --- CONSTRUCTED RESPONSE PROMPTS FIRESTORE HELPERS ---

export async function getConstructedResponsesForPaperFromFirestore(paperId: string): Promise<ConstructedResponsePrompt[]> {
  try {
    const paperRef = doc(db, 'praxisPapers', paperId);
    const snap = await getDoc(paperRef);
    if (snap.exists()) {
      const data = snap.data();
      if (Array.isArray(data.constructedResponsePrompts)) {
        return data.constructedResponsePrompts;
      }
    }
  } catch (err) {
    console.warn(`Error fetching constructed responses for paper ${paperId}:`, err);
  }
  const staticPaper = PRAXIS_PAPERS.find(p => p.id === paperId || p.code === paperId);
  if (staticPaper && Array.isArray(staticPaper.constructedResponsePrompts)) {
    return staticPaper.constructedResponsePrompts;
  }
  return [];
}

export async function saveConstructedResponseToFirestore(
  paperId: string,
  prompt: Omit<ConstructedResponsePrompt, 'id'> & { id?: string }
): Promise<string> {
  try {
    const currentPrompts = await getConstructedResponsesForPaperFromFirestore(paperId);
    const promptId = prompt.id || `cr_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const newPrompt: ConstructedResponsePrompt = {
      id: promptId,
      paperId,
      domain: prompt.domain,
      promptText: prompt.promptText,
      rubricNotes: prompt.rubricNotes || '',
      timeLimitMinutes: prompt.timeLimitMinutes || 10
    };

    const existingIdx = currentPrompts.findIndex(p => p.id === promptId);
    let updated: ConstructedResponsePrompt[];
    if (existingIdx >= 0) {
      updated = [...currentPrompts];
      updated[existingIdx] = newPrompt;
    } else {
      updated = [...currentPrompts, newPrompt];
    }

    const paperRef = doc(db, 'praxisPapers', paperId);
    await setDoc(paperRef, { constructedResponsePrompts: updated }, { merge: true });
    return promptId;
  } catch (err) {
    console.error('Error saving constructed response prompt to Firestore:', err);
    throw err;
  }
}

export async function saveBatchConstructedResponsesToFirestore(
  paperId: string,
  prompts: (Omit<ConstructedResponsePrompt, 'id'> & { id?: string })[]
): Promise<void> {
  try {
    const currentPrompts = await getConstructedResponsesForPaperFromFirestore(paperId);
    let updated = [...currentPrompts];

    for (const prompt of prompts) {
      const promptId = prompt.id || `cr_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
      const newPrompt: ConstructedResponsePrompt = {
        id: promptId,
        paperId,
        domain: prompt.domain,
        promptText: prompt.promptText,
        rubricNotes: prompt.rubricNotes || '',
        timeLimitMinutes: prompt.timeLimitMinutes || 10
      };

      const existingIdx = updated.findIndex(p => p.id === promptId);
      if (existingIdx >= 0) {
        updated[existingIdx] = newPrompt;
      } else {
        updated.push(newPrompt);
      }
    }

    const paperRef = doc(db, 'praxisPapers', paperId);
    await setDoc(paperRef, { constructedResponsePrompts: updated }, { merge: true });
  } catch (err) {
    console.error('Error batch saving constructed responses to Firestore:', err);
    throw err;
  }
}

export async function deleteConstructedResponseFromFirestore(paperId: string, promptId: string): Promise<void> {
  try {
    const currentPrompts = await getConstructedResponsesForPaperFromFirestore(paperId);
    const updated = currentPrompts.filter(p => p.id !== promptId);
    const paperRef = doc(db, 'praxisPapers', paperId);
    await setDoc(paperRef, { constructedResponsePrompts: updated }, { merge: true });
  } catch (err) {
    console.error('Error deleting constructed response prompt from Firestore:', err);
    throw err;
  }
}

export async function uploadStudyGuidePdfToStorage(file: File, paperId: string): Promise<{ storagePath: string; name: string }> {
  try {
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `praxisContent/studyGuides/${paperId}/${Date.now()}_${sanitizedName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);

    // Deliberately NOT calling getDownloadURL() here (2026-09-20 fix): that
    // mints a permanent, token-based public link which bypasses Storage
    // security rules entirely once issued -- anyone who ever sees that URL
    // can read the file forever, subscription or not. Confirmed live: a
    // guide Peter uploaded as admin opened for anyone he shared the raw
    // link with, no login or payment required. Study guide PDFs are now
    // only ever served through the authenticated GET
    // /api/study-guides/:guideId/pdf endpoint in server.ts (see
    // getStudyGuidePdfBlobUrl below), which re-checks the same
    // subscription/unlock gate as the studyGuides Firestore rule on every
    // single request instead of handing out a standing public link.
    return {
      storagePath,
      name: file.name
    };
  } catch (err) {
    console.error('Error uploading study guide PDF:', err);
    throw err;
  }
}

// Fetches a study guide's actual PDF bytes through the authenticated
// server endpoint (never via a public Storage download-link) and returns a
// local, per-tab blob: URL for use as an <iframe> src or download href.
// Throws with a user-facing message on auth/subscription/network failure --
// callers should catch and display err.message rather than swallow it.
export async function getStudyGuidePdfBlobUrl(guideId: string): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in to view this study guide.');
  }
  const idToken = await user.getIdToken();
  const res = await fetch(`/api/study-guides/${encodeURIComponent(guideId)}/pdf`, {
    headers: { Authorization: `Bearer ${idToken}` }
  });
  if (!res.ok) {
    let message = 'Unable to load this study guide PDF.';
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // response wasn't JSON -- keep the generic message
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Shared SM-2-inspired interval stepping, used identically for question mastery
 * and flashcard mastery so both review queues advance on the same schedule
 * (1d -> 3d -> 7d -> 14d -> 30d) and can be combined into one "due today" count.
 */
function stepSpacedRepetitionInterval(
  prev: { timesCorrect?: number; timesIncorrect?: number; consecutiveCorrect?: number; intervalDays?: number } | undefined,
  wasCorrect: boolean
): { timesCorrect: number; timesIncorrect: number; consecutiveCorrect: number; intervalDays: number; nextReviewDue: string } {
  const timesCorrect = (prev?.timesCorrect || 0) + (wasCorrect ? 1 : 0);
  const timesIncorrect = (prev?.timesIncorrect || 0) + (wasCorrect ? 0 : 1);

  let consecutiveCorrect: number;
  let intervalDays: number;

  if (wasCorrect) {
    consecutiveCorrect = (prev?.consecutiveCorrect || 0) + 1;
    const prevInterval = prev?.intervalDays || 0;
    if (prevInterval <= 0) intervalDays = 1;
    else if (prevInterval === 1) intervalDays = 3;
    else if (prevInterval === 3) intervalDays = 7;
    else if (prevInterval === 7) intervalDays = 14;
    else intervalDays = 30; // prevInterval >= 14
  } else {
    consecutiveCorrect = 0;
    intervalDays = 1;
  }

  const nextDueMs = Date.now() + intervalDays * 24 * 60 * 60 * 1000;
  const nextReviewDue = new Date(nextDueMs).toISOString();

  return { timesCorrect, timesIncorrect, consecutiveCorrect, intervalDays, nextReviewDue };
}

/**
 * Records a single per-question attempt in users/{uid}/questionAttempts/{attemptId}
 * and atomically updates aggregate mastery statistics in users/{uid}/questionMastery/{questionId}.
 */
export async function recordQuestionAttempt(
  uid: string,
  attempt: Omit<QuestionAttempt, 'id'>
): Promise<string> {
  if (!uid || attempt.selectedAnswerIndex === undefined || attempt.selectedAnswerIndex === null || attempt.selectedAnswerIndex < 0) return '';
  try {
    const attemptColRef = collection(db, 'users', uid, 'questionAttempts');
    const newAttemptRef = doc(attemptColRef);
    const masteryRef = doc(db, 'users', uid, 'questionMastery', attempt.questionId);

    await runTransaction(db, async (transaction) => {
      const masterySnap = await transaction.get(masteryRef);
      const stepped = stepSpacedRepetitionInterval(masterySnap.exists() ? masterySnap.data() : undefined, attempt.wasCorrect);

      const masteryData: QuestionMastery = {
        questionId: attempt.questionId,
        // 2026-10-08: lets the Dashboard / Review Queue load only the papers
        // a student has practised.
        paperId: attempt.paperId,
        domain: attempt.domain,
        timesCorrect: stepped.timesCorrect,
        timesIncorrect: stepped.timesIncorrect,
        consecutiveCorrect: stepped.consecutiveCorrect,
        intervalDays: stepped.intervalDays,
        lastAttemptedAt: attempt.timestamp,
        lastResult: attempt.wasCorrect ? 'correct' : 'incorrect',
        nextReviewDue: stepped.nextReviewDue
      };

      transaction.set(newAttemptRef, {
        ...attempt,
        id: newAttemptRef.id
      });

      transaction.set(masteryRef, masteryData, { merge: true });
    });

    return newAttemptRef.id;
  } catch (err) {
    console.warn('Error recording question attempt in Firestore transaction:', err);
    return '';
  }
}

/**
 * Fetches all question mastery records for a user from Firestore.
 */
export async function getUserQuestionMastery(uid: string): Promise<QuestionMastery[]> {
  if (!uid || uid === 'guest_user_1') return [];
  try {
    const colRef = collection(db, 'users', uid, 'questionMastery');
    const snap = await getDocs(colRef);
    return snap.docs.map(doc => doc.data() as QuestionMastery);
  } catch (err) {
    console.warn('Error fetching question mastery records from Firestore:', err);
    return [];
  }
}

/**
 * Records a single self-graded flashcard review ("Got it" / "Missed it") in
 * users/{uid}/flashcardReviews/{reviewId} and atomically updates aggregate
 * spaced-repetition state in users/{uid}/flashcardMastery/{flashcardId}.
 * Mirrors recordQuestionAttempt exactly so the two review queues can be
 * combined into a single "due today" count in the review UI.
 */
export async function recordFlashcardReview(
  uid: string,
  review: Omit<FlashcardReview, 'id'>
): Promise<string> {
  if (!uid || !review.flashcardId) return '';
  try {
    const reviewColRef = collection(db, 'users', uid, 'flashcardReviews');
    const newReviewRef = doc(reviewColRef);
    const masteryRef = doc(db, 'users', uid, 'flashcardMastery', review.flashcardId);

    await runTransaction(db, async (transaction) => {
      const masterySnap = await transaction.get(masteryRef);
      const stepped = stepSpacedRepetitionInterval(masterySnap.exists() ? masterySnap.data() : undefined, review.gotItRight);

      const masteryData: FlashcardMastery = {
        flashcardId: review.flashcardId,
        timesCorrect: stepped.timesCorrect,
        timesIncorrect: stepped.timesIncorrect,
        consecutiveCorrect: stepped.consecutiveCorrect,
        intervalDays: stepped.intervalDays,
        lastReviewedAt: review.timestamp,
        lastResult: review.gotItRight ? 'correct' : 'incorrect',
        nextReviewDue: stepped.nextReviewDue
      };

      transaction.set(newReviewRef, {
        ...review,
        id: newReviewRef.id
      });

      transaction.set(masteryRef, masteryData, { merge: true });
    });

    return newReviewRef.id;
  } catch (err) {
    console.warn('Error recording flashcard review in Firestore transaction:', err);
    return '';
  }
}

/**
 * Fetches all flashcard mastery records for a user from Firestore.
 */
export async function getUserFlashcardMastery(uid: string): Promise<FlashcardMastery[]> {
  if (!uid || uid === 'guest_user_1') return [];
  try {
    const colRef = collection(db, 'users', uid, 'flashcardMastery');
    const snap = await getDocs(colRef);
    return snap.docs.map(doc => doc.data() as FlashcardMastery);
  } catch (err) {
    console.warn('Error fetching flashcard mastery records from Firestore:', err);
    return [];
  }
}

/**
 * Anonymous and Guest Session Identification for preview gating
 */
// ---------------------------------------------------------------------
// Free preview helpers (2026-10-08 guest preview round)
// ---------------------------------------------------------------------

/** Authorization header for the preview endpoints when someone is signed in. */
export async function getPreviewAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

const guestClaimPromises = new Map<string, Promise<void>>();

/**
 * After a guest signs up or logs in, carry their free questions over to the
 * account (and earn 5 more on that paper). Safe to call many times: it runs
 * once per account per browser, and TestSimulator awaits it before loading
 * preview questions so the bonus is always in place first.
 */
export function ensureGuestPreviewClaimed(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return Promise.resolve();
  let guestId = '';
  try {
    guestId = localStorage.getItem('prepraxis_anon_session_id') || '';
  } catch {
    guestId = '';
  }
  if (!guestId) return Promise.resolve();
  const doneKey = `pp_guest_claimed_${guestId}`;
  try {
    if (localStorage.getItem(doneKey) === user.uid) return Promise.resolve();
  } catch {
    /* storage blocked: just try the claim */
  }
  const memoKey = `${user.uid}:${guestId}`;
  const existing = guestClaimPromises.get(memoKey);
  if (existing) return existing;
  const task = (async () => {
    try {
      const headers = await getPreviewAuthHeaders();
      const res = await fetch('/api/preview/claim-guest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ guestIdentifier: guestId })
      });
      if (res.ok) {
        try { localStorage.setItem(doneKey, user.uid); } catch { /* ignore */ }
      } else {
        guestClaimPromises.delete(memoKey);
      }
    } catch (err) {
      console.warn('[Preview] Could not carry over guest progress:', err);
      guestClaimPromises.delete(memoKey);
    }
  })();
  guestClaimPromises.set(memoKey, task);
  return task;
}

export function getOrCreateAnonymousSessionId(): string {
  try {
    const existing = localStorage.getItem('prepraxis_anon_session_id');
    if (existing && existing.trim().length > 0) {
      return existing.trim();
    }
    const newId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    localStorage.setItem('prepraxis_anon_session_id', newId);
    return newId;
  } catch (e) {
    return 'anon_' + Date.now();
  }
}

/**
 * Gets preview question usage document for a given identifier (guest sessionId or user UID) and paperId.
 */
export async function getPreviewQuestionUsage(identifier: string, paperId: string): Promise<PreviewUsage> {
  const defaultUsage: PreviewUsage = {
    id: `${identifier}_${paperId}`,
    identifier,
    paperId,
    usedQuestionCount: 0,
    attemptedQuestionIds: [],
    lastAttemptedAt: new Date().toISOString()
  };

  if (!identifier || !paperId) return defaultUsage;

  try {
    const docRef = doc(db, 'previewUsage', `${identifier}_${paperId}`);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data() as Partial<PreviewUsage>;
      return {
        id: snap.id,
        identifier: data.identifier || identifier,
        paperId: data.paperId || paperId,
        usedQuestionCount: typeof data.usedQuestionCount === 'number' ? data.usedQuestionCount : (data.attemptedQuestionIds?.length || 0),
        attemptedQuestionIds: Array.isArray(data.attemptedQuestionIds) ? data.attemptedQuestionIds : [],
        lastAttemptedAt: data.lastAttemptedAt || new Date().toISOString(),
        migratedToUserId: data.migratedToUserId
      };
    }
  } catch (err) {
    console.warn('Error fetching preview question usage:', err);
  }
  return defaultUsage;
}

/**
 * Records an attempted question in the 5-question free preview gate.
 */
export async function recordPreviewQuestionAttempt(identifier: string, paperId: string, questionId: string): Promise<PreviewUsage> {
  const current = await getPreviewQuestionUsage(identifier, paperId);
  const attemptedSet = new Set(current.attemptedQuestionIds);
  const isNewQuestion = !attemptedSet.has(questionId);
  if (isNewQuestion) {
    attemptedSet.add(questionId);
  }

  const updated: PreviewUsage = {
    ...current,
    usedQuestionCount: attemptedSet.size,
    attemptedQuestionIds: Array.from(attemptedSet),
    lastAttemptedAt: new Date().toISOString()
  };

  try {
    const docRef = doc(db, 'previewUsage', `${identifier}_${paperId}`);
    await setDoc(docRef, updated, { merge: true });
  } catch (err) {
    console.warn('Error persisting preview question attempt:', err);
  }

  return updated;
}

/**
 * When an anonymous guest registers/logs in, migrate their preview usage counts
 * so they continue with their existing attempt progress rather than resetting.
 */
export async function migratePreviewUsageToUser(anonymousSessionId: string, userId: string): Promise<void> {
  if (!anonymousSessionId || !userId || anonymousSessionId === userId) return;
  try {
    const colRef = collection(db, 'previewUsage');
    const q = query(colRef, where('identifier', '==', anonymousSessionId));
    const snap = await getDocs(q);
    if (!snap.empty) {
      for (const d of snap.docs) {
        const data = d.data() as PreviewUsage;
        const userDocRef = doc(db, 'previewUsage', `${userId}_${data.paperId}`);
        const userDocSnap = await getDoc(userDocRef);

        let mergedAttempted = data.attemptedQuestionIds || [];
        if (userDocSnap.exists()) {
          const existingUserData = userDocSnap.data() as PreviewUsage;
          const set = new Set([...(existingUserData.attemptedQuestionIds || []), ...mergedAttempted]);
          mergedAttempted = Array.from(set);
        }

        await setDoc(userDocRef, {
          id: `${userId}_${data.paperId}`,
          identifier: userId,
          paperId: data.paperId,
          usedQuestionCount: mergedAttempted.length,
          attemptedQuestionIds: mergedAttempted,
          lastAttemptedAt: new Date().toISOString()
        }, { merge: true });

        // Flag the anonymous record as migrated
        await setDoc(d.ref, { migratedToUserId: userId }, { merge: true });
      }
    }
  } catch (err) {
    console.warn('Error migrating preview question usage to user:', err);
  }
}

/**
 * Feed Events helpers for the Live Activity Feed
 */
export async function addFeedEvent(event: Omit<FeedEvent, 'id'>): Promise<string> {
  try {
    const colRef = collection(db, 'feedEvents');
    const docRef = await addDoc(colRef, {
      ...event,
      timestamp: event.timestamp || new Date().toISOString(),
      visibility: event.visibility || 'public'
    });
    return docRef.id;
  } catch (err) {
    console.warn('Error adding feed event:', err);
    return '';
  }
}

export async function getFeedEventsFromFirestore(limitCount: number = 20): Promise<FeedEvent[]> {
  try {
    const colRef = collection(db, 'feedEvents');
    const q = query(
      colRef,
      where('visibility', '==', 'public'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as FeedEvent));
    }
  } catch (err) {
    // Fallback without composite index if needed
    try {
      const colRef = collection(db, 'feedEvents');
      const q = query(colRef, orderBy('timestamp', 'desc'), limit(limitCount));
      const snap = await getDocs(q);
      if (!snap.empty) {
        return snap.docs
          .map(d => ({ id: d.id, ...d.data() } as FeedEvent))
          .filter(e => e.visibility === 'public');
      }
    } catch (fallbackErr) {
      console.warn('Error retrieving feed events from Firestore:', fallbackErr);
    }
  }
  return [];
}

/**
 * Reviews helpers (pulls only real reviews, returns empty array if none exist)
 */
export async function getVerifiedReviewsFromFirestore(): Promise<Review[]> {
  try {
    const colRef = collection(db, 'reviews');
    const q = query(colRef, orderBy('createdAt', 'desc'), limit(10));
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Review));
    }
  } catch (err) {
    console.warn('Error retrieving reviews from Firestore:', err);
  }
  return [];
}

/**
 * Blog post helpers.
 * Posts live in a top-level 'blogPosts' collection, independent of any paper.
 * Only the admin can write (enforced by Firestore security rules); the public
 * BlogPage reads published posts only.
 */
export async function getBlogPostsFromFirestore(includeUnpublished: boolean = false): Promise<BlogPostRecord[]> {
  try {
    const colRef = collection(db, 'blogPosts');
    // IMPORTANT: Firestore security rules that depend on `resource.data.status`
    // can only authorize a collection query (`list`) if the query ITSELF is
    // constrained with a matching `where()` clause -- an unconstrained
    // "fetch everything, filter client-side" query is rejected outright
    // (permission-denied) for any non-admin/unauthenticated caller, even
    // though individual documents would pass the rule. So for the public
    // (non-admin) read path we must filter at the Firestore level.
    const snap = includeUnpublished
      ? await getDocs(colRef)
      : await getDocs(query(colRef, where('status', '==', 'published')));
    const posts = snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogPostRecord));
    const filtered = includeUnpublished ? posts : posts.filter(p => p.status === 'published');
    return filtered.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  } catch (err) {
    console.warn('Error fetching blog posts from Firestore:', err);
    return [];
  }
}

export async function saveBlogPostToFirestore(
  post: Omit<BlogPostRecord, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
): Promise<string> {
  try {
    // Strip `id` before writing: Firestore's addDoc/setDoc reject any field
    // whose value is `undefined`, and a plain `{ ...post }` spread would keep
    // an explicit `id: undefined` key (e.g. from `editingBlogId || undefined`)
    // as an own property, causing "Unsupported field value: undefined" on
    // every brand-new post.
    const { id, ...rest } = post;
    const now = new Date().toISOString();
    if (id) {
      const docRef = doc(db, 'blogPosts', id);
      await setDoc(docRef, { ...rest, updatedAt: now }, { merge: true });
      return id;
    } else {
      const colRef = collection(db, 'blogPosts');
      const res = await addDoc(colRef, { ...rest, createdAt: now, updatedAt: now });
      return res.id;
    }
  } catch (err) {
    console.error('Error saving blog post to Firestore:', err);
    throw err;
  }
}

export async function deleteBlogPostFromFirestore(postId: string): Promise<void> {
  try {
    const docRef = doc(db, 'blogPosts', postId);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting blog post from Firestore:', err);
    throw err;
  }
}
